import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {DefinitionsList as DefinitionsListShared} from '@github-ui/custom-properties-core/components/DefinitionsList'
import type {BusinessCustomPropertiesDefinitionsPagePayload} from '@github-ui/custom-properties-types'
import {businessOrgCustomPropertyDetailsPath, customPropertyDetailsPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Pagination} from '@primer/react'
import {useEffect, useState} from 'react'

import {DefinitionsList} from '../components/DefinitionsList'
import {DefinitionsPageHeader} from '../components/PropertiesHeaderPageTabs'
import {usePropertiesListResults} from '../hooks/use-properties-list'
import {usePropertySource} from '../hooks/use-property-source'
import {DefinitionsFilter} from './DefinitionsFilter'

const debounceAnnounce = debounce(announce, 300)

export function BusinessCustomPropertiesPage() {
  const {ownDefinitionsCount, ...payload} = useRoutePayload<BusinessCustomPropertiesDefinitionsPagePayload>()
  const showFilterDialog = useFeatureFlag('repos_list_show_filter_dialog')
  const enableSharedComponents = useFeatureFlag('custom_properties_shared_components')
  const {sourceName} = usePropertySource()

  const {
    payload: {totalCount, pageCount, definitions, currentQ},
    currentPage,
    fetchResults,
  } = usePropertiesListResults({initialPayload: payload})

  const [filterQuery, setFilterQuery] = useState(currentQ)

  useEffect(() => {
    if (enableSharedComponents) return
    debounceAnnounce(`${totalCount} ${totalCount === 1 ? 'property' : 'properties'} found.`)
  }, [definitions, totalCount, enableSharedComponents])

  if (enableSharedComponents) {
    return (
      <>
        <DefinitionsPageHeader
          enableSharedComponents
          permissions="definitions"
          ownDefinitionsCount={ownDefinitionsCount}
        />
        <DefinitionsFilter
          className="mb-3"
          variant={showFilterDialog ? 'full' : 'input'}
          filterValue={filterQuery}
          onChange={setFilterQuery}
          businessSlug={sourceName}
          onSubmit={request => fetchResults({page: 1, filterQuery: request.raw})}
        />

        <DefinitionsListShared
          totalCount={totalCount}
          definitions={definitions}
          renderItem={definition => (
            <DefinitionsListShared.Item
              key={definition.source.slug + definition.propertyName}
              settingsLevel="business"
              definition={definition}
              linkTo={
                definition.source.type === 'business'
                  ? customPropertyDetailsPath({
                      pathPrefix: 'enterprises',
                      sourceName,
                      propertyName: definition.propertyName,
                    })
                  : businessOrgCustomPropertyDetailsPath({
                      business: sourceName,
                      org: definition.source.slug,
                      propertyName: definition.propertyName,
                    })
              }
            />
          )}
        />

        {pageCount > 1 && (
          <Pagination
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={(e, page) => {
              e.preventDefault() // Prevent click event from navigating
              fetchResults({page, filterQuery})
            }}
          />
        )}
      </>
    )
  }

  return (
    <>
      <DefinitionsPageHeader permissions="definitions" ownDefinitionsCount={ownDefinitionsCount} betaLabel />
      <DefinitionsFilter
        className="mb-3"
        variant={showFilterDialog ? 'full' : 'input'}
        filterValue={filterQuery}
        onChange={setFilterQuery}
        businessSlug={sourceName}
        onSubmit={request => fetchResults({page: 1, filterQuery: request.raw})}
      />

      <DefinitionsList totalCount={totalCount} definitions={definitions} />
      {pageCount > 1 && (
        <Pagination
          currentPage={currentPage}
          pageCount={pageCount}
          onPageChange={(e, page) => {
            e.preventDefault() // Prevent click event from navigating
            fetchResults({page, filterQuery})
          }}
        />
      )}
    </>
  )
}

try{ BusinessCustomPropertiesPage.displayName ||= 'BusinessCustomPropertiesPage' } catch {}